import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { ActivationPackService } from '@/services/database/ActivationPackService';

interface PackQuickLinksSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

export const PackQuickLinksSection = ({
  pack,
  brand = '#4E9CFF',
}: PackQuickLinksSectionProps) => {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedPresentation, setCopiedPresentation] = useState(false);

  const targetUrl = ActivationPackService.getTargetUrl(pack);
  const presentationUrl = ActivationPackService.getPresentationUrl(pack);
  const phoneNumber = pack.agent?.phone?.phone_number;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'target' },
      );
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  const handleCopyPresentation = async () => {
    try {
      await navigator.clipboard.writeText(presentationUrl);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'presentation' },
      );
      setCopiedPresentation(true);
      toast.success('Activation page link copied');
      setTimeout(() => setCopiedPresentation(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyPhone = async () => {
    if (!phoneNumber) return;
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopiedPhone(true);
      toast.success('Phone number copied');
      setTimeout(() => setCopiedPhone(false), 2000);
    } catch {
      toast.error('Failed to copy phone number');
    }
  };

  return (
    <OverviewSectionCard
      title="Quick Links"
      subtitle="Copy URLs and contact information"
      genieIcon="share"
      brand={brand}
    >
      <div className="space-y-4">
        <OverviewSettingRow
          label="Target URL"
          value={
            <div className="flex items-center gap-2">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded border flex-1 truncate text-gray-700 max-w-[300px]">
                {targetUrl}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyUrl}
                className="shrink-0"
              >
                {copiedUrl ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          }
          fullWidth
        />

        <OverviewSettingRow
          label="Activation Page"
          value={
            <div className="flex items-center gap-2">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded border flex-1 truncate text-gray-700 max-w-[300px]">
                {presentationUrl}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyPresentation}
                className="shrink-0"
              >
                {copiedPresentation ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          }
          fullWidth
        />

        {phoneNumber && (
          <OverviewSettingRow
            label="Phone Number"
            value={
              <div className="flex items-center gap-2">
                <code className="text-xs bg-gray-100 px-2 py-1 rounded border text-gray-700">
                  {phoneNumber}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyPhone}
                  className="shrink-0"
                >
                  {copiedPhone ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            }
            fullWidth
          />
        )}
      </div>
    </OverviewSectionCard>
  );
};
